import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./RevenueReportPage.css";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import vi from "date-fns/locale/vi";
import { format } from "date-fns";
import { useLocalSearch } from "../../hooks/useLocalSearch";
import { exportToExcel } from "../../utils/excel";
import { useToast } from './../../components/Toast/ToastContainer';
import Pagination from "../../components/Pagination/Pagination";

registerLocale("vi", vi);

const RevenueReportPage = () => {
  // ================= STATE =================
  const [data, setData] = useState([]);
  const [accountNumberOptions, setAccountNumberOptions] = useState([]);
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: 10,
  });

  const [filters, setFilters] = useState({
    cifDaiLy: "",
    cifChuQuan: "",
    stk: "",
    loaiGD: "",
    fromDate: null,
    toDate: null,
  });

  const [summary, setSummary] = useState({
    tongPhatSinh: 0,
    doanhSoNop: 0,
    doanhSoRut: 0,
  });

  // ================= LOGIC VALIDATION & SEARCH =================

  // Kiểm tra xem tất cả các trường filter có trống không để disable nút
  const isFilterEmpty = useMemo(() => {
    return (
      !filters.cifDaiLy.trim() &&
      !filters.cifChuQuan.trim() &&
      !filters.stk &&
      !filters.loaiGD &&
      !filters.fromDate &&
      !filters.toDate
    );
  }, [filters]);

  // Tạo dữ liệu đã xử lý để tìm kiếm nhanh (local search) hỗ trợ tiếng Việt
  const processedData = useMemo(() => {
    return data.map((row) => {
      const typeVi = row.transaction_type === "CASH_IN" ? "Nộp tiền" : "Rút tiền";
      const dateVi = row.transaction_date ? format(new Date(row.transaction_date), "dd/MM/yyyy") : "";
      return {
        ...row,
        search_type: typeVi,
        search_amount: row.amount ? row.amount.toString() : "",
        search_date: dateVi,
        search_cif_agent: row.agent_cif,
        search_cif_kh: row.customer_cif,
      };
    });
  }, [data]);

  const { searchTerm, setSearchTerm, filteredData } = useLocalSearch(processedData);

  // ================= FETCH API =================

  const fetchSalesReports = useCallback(
    async (pageNumber = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.cifDaiLy) params.append("agent_cif", filters.cifDaiLy);
        if (filters.cifChuQuan) params.append("parent_cif", filters.cifChuQuan);
        if (filters.stk) params.append("customer_account", filters.stk);
        if (filters.loaiGD) {
          const typeMap = { nop: "CASH_IN", rut: "CASH_OUT" };
          params.append("transaction_type", typeMap[filters.loaiGD] || filters.loaiGD);
        }
        if (filters.fromDate) params.append("from_date", format(filters.fromDate, "yyyy-MM-dd'T'00:00:00"));
        if (filters.toDate) params.append("to_date", format(filters.toDate, "yyyy-MM-dd'T'23:59:59"));

        params.append("page", pageNumber);
        params.append("page_size", pagination.page_size);

        const response = await fetch(
          `https://api.bvb.cybertech.com.vn/api/v1/agent/reports/sales?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        const result = await response.json();

        if (result) {
          setData(result.data || []);
          setSummary({
            tongPhatSinh: parseFloat(result.summary?.total_amount || 0),
            doanhSoNop: parseFloat(result.summary?.cash_in_amount || 0),
            doanhSoRut: parseFloat(result.summary?.cash_out_amount || 0),
          });
          setPagination((prev) => ({
            ...prev,
            total: result.total || 0,
            page: pageNumber,
          }));
        }
      } catch (error) {
        console.error("Lỗi fetch API doanh số:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.page_size]
  );

  const fetchAccountNumberList = useCallback(async () => {
    if (!filters.cifDaiLy) return;
    try {
      const response = await fetch(
        `https://api.bvb.cybertech.com.vn/api/v1/admin/agents/accounts/${filters.cifDaiLy}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
        }
      );
      const result = await response.json();
      setAccountNumberOptions(Array.isArray(result) ? result.map((item) => item.account_number) : []);
    } catch (e) { console.error(e); }
  }, [filters.cifDaiLy]);

  useEffect(() => { fetchAccountNumberList(); }, [fetchAccountNumberList]);

  // ================= HANDLERS =================

  const handleChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
  const handleDateChange = (date, name) => {
    setFilters((prev) => {
      // Clone state cũ
      const newFilters = { ...prev, [name]: date };

      // 1. Kiểm tra logic cho 'fromDate' (Tên đúng trong state trang này)
      if (name === "fromDate") {
        // Nếu đã chọn toDate mà fromDate lại lớn hơn toDate -> Reset toDate
        if (prev.toDate && date && date > prev.toDate) {
          newFilters.toDate = null;
        }
      }

      // 2. Kiểm tra logic cho 'toDate'
      if (name === "toDate") {
        // Nếu đã chọn fromDate mà toDate lại nhỏ hơn fromDate -> Reset fromDate
        if (prev.fromDate && date && date < prev.fromDate) {
          newFilters.fromDate = null;
        }
      }

      return newFilters;
    });
  };
  const handleSearch = () => {
    if (isFilterEmpty) {
      toast.error("Vui lòng nhập ít nhất một thông tin để tìm kiếm.");
      return;
    }

    fetchSalesReports(1);
  };
  const handleReset = () => setFilters({ cifDaiLy: "", cifChuQuan: "", stk: "", loaiGD: "", fromDate: null, toDate: null });


  const handleExportExcel = async () => {
    if (isFilterEmpty) {
      toast.error("Vui lòng nhập ít nhất một điều kiện lọc trước khi xuất Excel.");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`https://api.bvb.cybertech.com.vn/api/v1/agent/reports/sales/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
      });
      const result = await response.json();
      const rawData = result.data || [];

      // LỌC THỦ CÔNG - Tối ưu hóa so sánh
      const filteredExportData = rawData.filter((item) => {
        // 1. Kiểm tra CIF Đại lý (Dữ liệu: "888888")
        const matchCifAgent = !filters.cifDaiLy ||
          String(item.agent_cif || "").toLowerCase().includes(filters.cifDaiLy.trim().toLowerCase());

        // 2. Kiểm tra CIF Chủ quản (Dữ liệu đang là null)
        // Nếu filter có nhập nhưng data là null -> không khớp
        // const matchCifParent = !filters.cifChuQuan || 
        //   String(item.parent_cif || "").toLowerCase().includes(filters.cifChuQuan.trim().toLowerCase());

        // 3. Kiểm tra Số tài khoản
        // const matchStk = !filters.stk || 
        //   String(item.customer_account || "").includes(filters.stk.trim());

        // 4. Kiểm tra Loại giao dịch
        const typeMap = { nop: "CASH_IN", rut: "CASH_OUT" };
        const matchType = !filters.loaiGD || item.transaction_type === typeMap[filters.loaiGD];

        // 5. Kiểm tra Ngày tháng (Dữ liệu: "2025-12-18T16:27:53")
        // Chuyển về getTime() để so sánh con số cho chính xác tuyệt đối
        const itemTime = new Date(item.transaction_date).getTime();

        const matchFromDate = !filters.fromDate ||
          itemTime >= new Date(filters.fromDate).setHours(0, 0, 0, 0);

        const matchToDate = !filters.toDate ||
          itemTime <= new Date(filters.toDate).setHours(23, 59, 59, 999);

        return matchCifAgent && matchType && matchFromDate && matchToDate;
      });

      if (filteredExportData.length === 0) {
        alert("Không có dữ liệu nào khớp với bộ lọc hiện tại trong danh sách tất cả.");
        setLoading(false);
        return;
      }

      // Mapping dữ liệu hiển thị giống bảng
      const excelData = filteredExportData.map((row) => ({
        ...row,
        parent_cif: row.parent_cif || "-", // Hiển thị dấu gạch nếu null
        transaction_date: row.transaction_date ? format(new Date(row.transaction_date), "dd/MM/yyyy HH:mm:ss") : "-",
        transaction_type: row.transaction_type === "CASH_IN" ? "Nộp tiền" : "Rút tiền",
        amount: new Intl.NumberFormat("vi-VN").format(row.amount || 0),
        fee: new Intl.NumberFormat("vi-VN").format(row.fee || 0),
      }));

      // Xuất file
      exportToExcel({
        data: excelData,
        fileName: `Bao_cao_doanh_so_${format(new Date(), "ddMMyyyy_HHmm")}.xlsx`,
        header: [
          { label: "CIF chủ quản", key: "parent_cif" },
          { label: "CIF AGENT", key: "agent_cif" },
          { label: "Loại giao dịch", key: "transaction_type" },
          { label: "CIF KH", key: "customer_cif" },
          { label: "Số giao dịch", key: "transaction_ref" },
          { label: "Số REF", key: "core_ref" },
          { label: "Trạng thái", key: "status" },
          { label: "Số tiền", key: "amount" },
          { label: "Phí VAT", key: "fee" },
          { label: "Số tài khoản", key: "customer_account" },
          { label: "Tên tài khoản", key: "customer_name" },
          { label: "Ngày giao dịch", key: "transaction_date" },
          { label: "Người thực hiện", key: "performed_by" }
        ]
      });
    } catch (e) {
      console.error(e);
      alert("Lỗi khi xử lý dữ liệu xuất file");
    } finally {
      setLoading(false);
    }
  };

  // ================= RENDER =================

  return (
    <div className="rev-container">
      <h2 className="page-title">Báo cáo Doanh số</h2>

      <div className="rev-filter-card">
        <div className="rev-grid">
          <div className="rev-item">
            <label>CIF Đại lý</label>
            <input name="cifDaiLy" value={filters.cifDaiLy} onChange={handleChange} placeholder="Nhập CIF đại lý" />
          </div>
          <div className="rev-item">
            <label>CIF chủ quản</label>
            <input name="cifChuQuan" value={filters.cifChuQuan} onChange={handleChange} placeholder="Nhập tên đại lý" />
          </div>
          <div className="rev-item">
            <label>Số tài khoản giao dịch</label>
            <select name="stk" value={filters.stk} onChange={handleChange}>
              <option value="">Chọn STK giao dịch</option>
              {accountNumberOptions.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          <div className="rev-item">
            <label>Loại giao dịch</label>
            <select name="loaiGD" value={filters.loaiGD} onChange={handleChange}>
              <option value="">Chọn loại</option>
              <option value="nop">Nộp tiền</option>
              <option value="rut">Rút tiền</option>
            </select>
          </div>
          <div className="rev-item">
            <label>Từ ngày</label>
            <div className="rev-date-wrapper">
              <img
                src="/icons/icons8-calendar-100.png"
                className="tr-date-icon"
                alt=""
              />
              <DatePicker selected={filters.fromDate} onChange={(d) => handleDateChange(d, "fromDate")} dateFormat="dd/MM/yyyy" placeholderText="dd/mm/yyyy" className="rev-datepicker" locale="vi"
                selectsStart
                startDate={filters.fromDate}      // Sửa thành fromDate
                endDate={filters.toDate}
                maxDate={filters.to_date ? filters.to_date : new Date()} />
            </div>
          </div>
          <div className="rev-item">
            <label>Đến ngày</label>
            <div className="rev-date-wrapper">
              <img
                src="/icons/icons8-calendar-100.png"
                className="tr-date-icon"
                alt=""
              />
              <DatePicker selected={filters.toDate} onChange={(d) => handleDateChange(d, "toDate")} dateFormat="dd/MM/yyyy" placeholderText="dd/mm/yyyy" className="rev-datepicker" locale="vi"
                selectsEnd
                startDate={filters.fromDate}      // Sửa thành fromDate
                endDate={filters.toDate}          // Sửa thành toDate
                minDate={filters.fromDate}
                maxDate={new Date()} />
            </div>
          </div>
        </div>
        <div className="rev-actions">
          <button className="btn-secondary" onClick={handleReset}>Xóa lọc</button>
          <button className="btn-primary" onClick={handleSearch} disabled={loading}>
            {loading ? "Đang tải..." : "Tìm kiếm"}
          </button>
        </div>
      </div>

      <div className="tr-result-header">
        <h3 className="result-title">Kết quả tìm kiếm</h3>
        <div className="tr-actions-right">
          <button className="excel-btn" onClick={handleExportExcel} disabled={loading}>Xuất Excel</button>
          <div className="tr-search-inline">
            <input placeholder="Tìm kiếm nhanh..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="rev-summary">
        <span>TỔNG PHÁT SINH: {summary.tongPhatSinh.toLocaleString("vi-VN")}</span>
        <span className="green">DOANH SỐ NỘP: {summary.doanhSoNop.toLocaleString("vi-VN")}</span>
        <span className="red">DOANH SỐ RÚT: {summary.doanhSoRut.toLocaleString("vi-VN")}</span>
      </div>

      <div className="rev-table-wrapper">
        <table className="rev-table">
          <thead>
            <tr>
              <th>CIF chủ quản</th><th>CIF AGENT</th><th>Loại giao dịch</th><th>CIF KH</th>
              <th>Số giao dịch</th><th>Số REF</th><th>Trạng thái</th><th>Số tiền</th>
              <th>Phí VAT</th><th>Số tài khoản</th><th>Tên tài khoản</th><th>Ngày giao dịch</th><th>Người thực hiện</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? filteredData.map((row, i) => (
              <tr key={i}>
                <td>{row.parent_cif || "-"}</td><td>{row.agent_cif}</td>
                <td>{row.transaction_type === "CASH_IN" ? "Nộp tiền" : "Rút tiền"}</td>
                <td>{row.customer_cif}</td><td>{row.transaction_ref}</td><td>{row.core_ref}</td>
                <td>{row.status == 'SUCCESS' ? 'Thành công':"---"}</td><td>{parseFloat(row.amount || 0).toLocaleString("vi-VN")}</td>
                <td>{parseFloat(row.fee || 0).toLocaleString("vi-VN")}</td><td>{row.customer_account}</td>
                <td>{row.customer_name}</td><td>{row.transaction_date ? format(new Date(row.transaction_date), "dd/MM/yyyy HH:mm:ss") : "-"}</td>
                <td>{row.performed_by}</td>
              </tr>
            )) : (
              <tr><td colSpan="13" style={{ textAlign: "center", padding: "20px" }}>{loading ? "Đang tải..." : "Không có dữ liệu"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pagination.page}
        pageSize={pagination.page_size}
        totalItems={pagination.total}
        filteredCount={filteredData.length}
        isSearching={!!searchTerm}
        loading={loading}
        onPageChange={(newPage) => fetchSalesReports(newPage)}
      />
    </div>
  );
};

export default RevenueReportPage;